import { useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import type { ContratoDto, ContratoOperadorDto, OperadorCatalogDto } from "@/services/locacoes";
import { locacoesTabContentClass, locacoesTableHeadClass, locacoesTableWrapClass } from "./locacoesUtils";
import { upperLocacaoText } from "./upperLocacaoText";
import { LocacoesStatCard } from "./LocacoesStatCard";

interface Props {
  contrato: ContratoDto;
  operadoresCatalog: OperadorCatalogDto[];
  busy?: boolean;
  onSaveOperadores: (operadores: ContratoOperadorDto[]) => Promise<void>;
}

function emptyOperador(): ContratoOperadorDto {
  return { id: `op-local-${Date.now()}`, nome: "", documento: "", telefone: "", observacao: "" };
}

export function TabOperadores({ contrato, operadoresCatalog, busy, onSaveOperadores }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ContratoOperadorDto | null>(null);
  const [form, setForm] = useState<ContratoOperadorDto>(emptyOperador());
  const [pendingDelete, setPendingDelete] = useState<ContratoOperadorDto | null>(null);

  const catalogOptions = useMemo(
    () => [
      { value: "", label: "Manual (sem vínculo ao cadastro)" },
      ...operadoresCatalog.map((o) => ({
        value: o.id,
        label: o.usuarioId ? `${o.nome} (com login)` : o.nome,
      })),
    ],
    [operadoresCatalog],
  );

  const applyCatalog = (catalogId: string) => {
    const cat = operadoresCatalog.find((o) => o.id === catalogId);
    if (!cat) {
      setForm((f) => ({ ...f, catalogOperadorId: undefined }));
      return;
    }
    setForm((f) => ({
      ...f,
      catalogOperadorId: cat.id,
      nome: upperLocacaoText(cat.nome),
      documento: cat.documento,
      telefone: cat.telefone,
      observacao: upperLocacaoText(cat.observacao || f.observacao),
    }));
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyOperador());
    setModalOpen(true);
  };

  const openEdit = (op: ContratoOperadorDto) => {
    setEditing(op);
    setForm({ ...op, nome: upperLocacaoText(op.nome), observacao: upperLocacaoText(op.observacao) });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) return;
    const list = [...contrato.operadores];
    const payload = { ...form, nome: form.nome.trim() };
    if (editing) {
      const idx = list.findIndex((o) => o.id === editing.id);
      if (idx >= 0) list[idx] = payload;
    } else {
      list.push({ ...payload, id: form.id.startsWith("op-local-") ? form.id : `op-${Date.now()}` });
    }
    await onSaveOperadores(list);
    setModalOpen(false);
  };

  const handleDelete = (op: ContratoOperadorDto) => setPendingDelete(op);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const op = pendingDelete;
    setPendingDelete(null);
    await onSaveOperadores(contrato.operadores.filter((o) => o.id !== op.id));
  };

  const catalogNome = (op: ContratoOperadorDto) => {
    if (!op.catalogOperadorId) return null;
    return operadoresCatalog.find((c) => c.id === op.catalogOperadorId)?.nome ?? null;
  };

  return (
    <div className={locacoesTabContentClass}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <LocacoesStatCard
          title="Operadores no contrato"
          value={contrato.operadores.length}
          icon={<Users className="h-5 w-5" />}
          accentClass="border-l-4 border-l-blue-400"
        />
        <LocacoesStatCard
          title="Vinculados ao cadastro"
          value={contrato.operadores.filter((o) => o.catalogOperadorId).length}
          icon={<Users className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
        />
        <LocacoesStatCard
          title="Cadastro manual"
          value={contrato.operadores.filter((o) => !o.catalogOperadorId).length}
          icon={<Users className="h-5 w-5" />}
          accentClass="border-l-4 border-l-purple-400"
        />
      </div>
      <div className="flex justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openNew}>
          Novo operador
        </Button>
      </div>
      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Cadastro</th>
              <th className="px-4 py-3">Documento</th>
              <th className="px-4 py-3">Telefone</th>
              <th className="px-4 py-3">Observação</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {contrato.operadores.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Nenhum operador cadastrado neste contrato.
                </td>
              </tr>
            ) : (
              contrato.operadores.map((op) => (
                <tr key={op.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-slate-800">{op.nome}</td>
                  <td className="px-4 py-3 text-slate-600">{catalogNome(op) ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{op.documento || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{op.telefone || "—"}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-slate-500">{op.observacao || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(op)} leftIcon={<Pencil className="h-3.5 w-3.5" />}>
                        Editar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(op)} leftIcon={<Trash2 className="h-3.5 w-3.5" />}>
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-brand-600" />
            <span>{editing ? "Editar operador" : "Novo operador"}</span>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void handleSave()} loading={busy} disabled={!form.nome.trim()}>
              Salvar
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <SearchableSelect
            label="Operador do cadastro"
            value={form.catalogOperadorId ?? ""}
            options={catalogOptions}
            onChange={(catalogOperadorId) => {
              if (!catalogOperadorId) {
                setForm((f) => ({ ...f, catalogOperadorId: undefined }));
                return;
              }
              applyCatalog(catalogOperadorId);
            }}
            placeholder="Selecionar do cadastro global…"
          />
          <Input label="Nome *" uppercase value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
          <Input label="Documento" value={form.documento} onChange={(e) => setForm((f) => ({ ...f, documento: e.target.value }))} />
          <Input label="Telefone" value={form.telefone} onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))} />
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observação</label>
            <textarea
              value={form.observacao}
              onChange={(e) => setForm((f) => ({ ...f, observacao: upperLocacaoText(e.target.value) }))}
              rows={2}
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={pendingDelete != null}
        title="Excluir operador"
        message={pendingDelete ? `Excluir operador "${pendingDelete.nome}"?` : ""}
        confirmLabel="Excluir"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        loading={busy}
      />
    </div>
  );
}
